import React from 'react';
import { DealDetailsResponseDto } from '../../../lib/interfaces/deals';
import { Badge } from '../../../ui-components/ui/badge';
import { Building2, User, Briefcase } from 'lucide-react';
import { cn } from '../../../lib/utils/utils';

interface DealHeaderSimpleProps {
  deal: DealDetailsResponseDto;
  ownerName?: string;
}

export const DealHeaderSimple: React.FC<DealHeaderSimpleProps> = ({ deal, ownerName }) => {
  const getStageColor = (stageName: string) => {
    // You can customize these colors based on your stage names
    const stageColors: { [key: string]: string } = {
      'Kontaktiert': 'bg-blue-100 text-blue-800',
      'Qualifiziert': 'bg-purple-100 text-purple-800',
      'Angebot': 'bg-orange-100 text-orange-800',
      'Verhandlung': 'bg-yellow-100 text-yellow-800',
      'Gewonnen': 'bg-green-100 text-green-800',
      'Verloren': 'bg-red-100 text-red-800',
    };
    return stageColors[stageName] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white border-b">
      <div className="px-6 py-4 space-y-3">
        {/* Deal Info Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Stage Badge */}
            {deal.stageTitle && (
              <Badge className={cn("text-sm", getStageColor(deal.stageTitle))}>
                {deal.stageTitle}
              </Badge>
            )}
            
            {/* Pipeline */}
            {deal.pipelineTitle && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Briefcase className="h-4 w-4" />
                <span>{deal.pipelineTitle}</span>
              </div>
            )}
          </div>

          {/* Status Badges */}
          <div className="flex items-center gap-2">
            {deal.isWon && (
              <Badge className="bg-green-100 text-green-800">Gewonnen</Badge>
            )}
            {deal.isArchived && (
              <Badge variant="secondary">Archiviert</Badge>
            )}
          </div>
        </div>

        {/* Owner and Contact Info */}
        <div className="flex items-center gap-6 text-sm text-gray-600">
          {/* Owner */}
          {(ownerName || deal.belongsToUserFullName) && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="text-gray-500">Besitzer:</span>
              <span className="font-medium">{ownerName || deal.belongsToUserFullName}</span>
            </div>
          )}

          {/* Contact */}
          {deal.summaryPreview?.personSummary && (
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="text-gray-500">Kontakt:</span>
              <span className="font-medium">{deal.summaryPreview.personSummary.title}</span>
            </div>
          )}

          {/* Organization */}
          {deal.summaryPreview?.organizationSummary && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              <span className="text-gray-500">Unternehmen:</span>
              <span className="font-medium">{deal.summaryPreview.organizationSummary.title}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealHeaderSimple;
